import { createContext, useCallback, useContext, useMemo } from "react";
import { PackagingGridColumns, usePackagingColumns } from "./usePackagingColumns";
import {
    PackagingList,
    useGetAllPackaging,
    useGetAllPackagingComposites,
    useGetAllPackagingElements,
} from "src/api/client-api/packagings/useGetAllPackagings";
import { useGridColumns, GridColumnsType } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { DelayedSearch, useDelayedSearch } from "../../../utils/useDelayedSearch";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { usePackagingsMenuItems } from "./PackagingsGrid/usePackaginsMenuItems";
import { useUrlToApi } from "src/api/_common/params/useUrlToApi";
import { MenuItemType } from "src/components/ActionsPopover/ActionsPopover.types";
import { AppSearch } from "src/api/_common/hydra/search";
import { ApiPagination } from "src/api/_common/params/useApiPaginationUrlSync";
import { useSearchParams } from "react-router";
import { PAGINATION_PAGE } from "src/_Globals/urls";
import { useTranslation } from "react-i18next";
import type { ComponentAppearance } from "src/components/appearance";
import type { TableAction } from "src/components/Grid/headers/GridTableActions";
import { useListExportAction } from "src/components/Grid/headers/useListExportAction";
import { packagingListExportTarget } from "./packagingListExportTarget";

export type PackagingListTabId = "all" | "elements" | "composites";

export type PackagingListTab = {
    id: PackagingListTabId;
    label: string;
    count: number;
};

const PACKAGING_TAB_PARAM = "tab" as const;

const isPackagingListTabId = (value: string | null): value is PackagingListTabId => {
    return value === "all" || value === "elements" || value === "composites";
};

type PackagingListContextInitialValues = {
    mrt_columns: PackagingGridColumns;
    gridConfiguration: GridColumnsType<PackagingList>;

    totalItems: number;
    packagings: PackagingList[];
    isRefetching: boolean;
    isReady: boolean;
    hasData: boolean;
    hasError: boolean;
    retryActiveQuery: () => void;
    menuItems: MenuItemType<PackagingList>[];
    tableActions: TableAction[];

    globalSearch: DelayedSearch;
    appSearch: AppSearch | undefined;

    gridKey: string;
    pagination: ApiPagination;

    activeTab: PackagingListTabId;
    setActiveTab: (tab: PackagingListTabId) => void;
    tabs: PackagingListTab[];
    isTabsLoading: boolean;
    hasTabsError: boolean;
    retryTabsQueries: () => void;
};
const PackagingListContext = createContext<PackagingListContextInitialValues | null>(null);

type PackagingListContextProviderProps = { appearance?: ComponentAppearance; children: ReactNode };
export const PackagingListContextProvider = (props: PackagingListContextProviderProps) => {
    const { appearance = "legacy", children } = props;
    const { t } = useTranslation();
    const { canUser, currentCampaign, selectedCompany } = useFoodpilotContext();
    const { company } = useCompanyMetadata();
    const [searchParams, setSearchParams] = useSearchParams();

    const resetPaginationForSearch = useCallback(() => {
        setSearchParams(
            (currentSearchParams) => {
                const nextSearchParams = new URLSearchParams(currentSearchParams);
                nextSearchParams.delete(PAGINATION_PAGE);
                return nextSearchParams;
            },
            { replace: true },
        );
    }, [setSearchParams]);
    const globalSearch = useDelayedSearch({ actionOnDelay: resetPaginationForSearch });
    const { allAsAxiosParams, filters, sort, pagination } = useUrlToApi({ page: "1", itemsPerPage: "20" });

    const tabFromUrl = searchParams.get(PACKAGING_TAB_PARAM);
    const activeTab: PackagingListTabId = isPackagingListTabId(tabFromUrl) ? tabFromUrl : "all";
    const setActiveTab = useCallback(
        (tab: PackagingListTabId) => {
            const newSearchParams = new URLSearchParams(searchParams);
            if (tab === "all") {
                newSearchParams.delete(PACKAGING_TAB_PARAM);
            } else {
                newSearchParams.set(PACKAGING_TAB_PARAM, tab);
            }
            newSearchParams.delete(PAGINATION_PAGE);
            setSearchParams(newSearchParams);
        },
        [searchParams, setSearchParams],
    );

    const commonListOptions = {
        cacheTime: 0,
        search: globalSearch.delayedSearch,
        params: {
            ...allAsAxiosParams,
            campaign: currentCampaign?.id,
        },
        scoresQuery: {
            scores: {
                subscores: false,
            },
        },
        // sorting: sorting,
    };

    const allPackagingsQuery = useGetAllPackaging({
        ...commonListOptions,
        enabled: activeTab === "all",
    });
    const packagingCompositesQuery = useGetAllPackagingComposites({
        ...commonListOptions,
        enabled: activeTab === "composites",
    });
    const packagingElementsQuery = useGetAllPackagingElements({
        ...commonListOptions,
        enabled: activeTab === "elements",
    });

    const allPackagingsCountQuery = useGetAllPackaging({
        cacheTime: 0,
        search: globalSearch.delayedSearch,
        params: {
            itemsPerPage: "1",
            campaign: currentCampaign?.id,
        },
    });
    const packagingCompositesCountQuery = useGetAllPackagingComposites({
        cacheTime: 0,
        search: globalSearch.delayedSearch,
        params: {
            itemsPerPage: "1",
            campaign: currentCampaign?.id,
        },
    });
    const packagingElementsCountQuery = useGetAllPackagingElements({
        cacheTime: 0,
        search: globalSearch.delayedSearch,
        params: {
            itemsPerPage: "1",
            campaign: currentCampaign?.id,
        },
    });

    const activePackagingsQuery =
        activeTab === "composites" ? packagingCompositesQuery
        : activeTab === "elements" ? packagingElementsQuery
        : allPackagingsQuery;
    const { packagings, isReady, appSearch, totalItems, isRefetching } = activePackagingsQuery;

    const tabs: PackagingListTab[] = useMemo(
        () => [
            {
                id: "all",
                label: t("packagings.tabs.all", "All packagings"),
                count: allPackagingsCountQuery.totalItems ?? 0,
            },
            {
                id: "elements",
                label: t("packagings.tabs.components", "Components"),
                count: packagingElementsCountQuery.totalItems ?? 0,
            },
            {
                id: "composites",
                label: t("packagings.tabs.units", "Units"),
                count: packagingCompositesCountQuery.totalItems ?? 0,
            },
        ],
        [
            allPackagingsCountQuery.totalItems,
            packagingCompositesCountQuery.totalItems,
            packagingElementsCountQuery.totalItems,
            t,
        ],
    );
    const isTabsLoading =
        allPackagingsCountQuery.isReady === false ||
        packagingCompositesCountQuery.isReady === false ||
        packagingElementsCountQuery.isReady === false;
    const hasTabsError =
        allPackagingsCountQuery.isError || packagingCompositesCountQuery.isError || packagingElementsCountQuery.isError;
    const { refetch: refetchAllPackagingsCount } = allPackagingsCountQuery;
    const { refetch: refetchPackagingCompositesCount } = packagingCompositesCountQuery;
    const { refetch: refetchPackagingElementsCount } = packagingElementsCountQuery;
    const retryTabsQueries = useCallback(() => {
        void refetchAllPackagingsCount();
        void refetchPackagingCompositesCount();
        void refetchPackagingElementsCount();
    }, [refetchAllPackagingsCount, refetchPackagingCompositesCount, refetchPackagingElementsCount]);

    const gridKey = "packagings-grid";

    const mrt_packagingColumns = usePackagingColumns(packagings ?? [], gridKey);
    const gridConfiguration = useGridColumns(mrt_packagingColumns, gridKey);
    const menuItems = usePackagingsMenuItems({
        appearance,
        companyIds: {
            id: company.id,
            "@id": company["@id"],
        },
        canUser,
    });

    const exportAction = useListExportAction({
        ...packagingListExportTarget(activeTab),
        filters,
        sort,
        search: globalSearch.delayedSearch,
        campaignId: currentCampaign?.id,
        organizationId: selectedCompany?.id,
        isDisabled: currentCampaign === null || selectedCompany === null,
    });

    const items: PackagingListContextInitialValues = {
        mrt_columns: mrt_packagingColumns,
        gridConfiguration,

        totalItems: totalItems ?? 0,
        packagings: packagings ?? [],
        isReady,
        isRefetching,
        hasData: activePackagingsQuery.data !== undefined,
        hasError: activePackagingsQuery.isError,
        retryActiveQuery: activePackagingsQuery.refetch,
        menuItems,
        tableActions: [exportAction],
        appSearch,
        globalSearch,

        gridKey: gridKey,
        pagination,

        activeTab,
        setActiveTab,
        tabs,
        isTabsLoading,
        hasTabsError,
        retryTabsQueries,
    };

    return <PackagingListContext.Provider value={items}>{children}</PackagingListContext.Provider>;
};

export const usePackagingListContext = () => {
    const context = useContext(PackagingListContext);

    if (context) {
        return context;
    }

    throw new Error(`usePackagingListContext must be used within an PackagingListContextProvider`);
};
